// Web view of the FTUE network-feed recommendation (FTUE Network Feed Analysis
// spec, "Recommendation UI"; web read/apply task).
//
// A thin web rendering of the SAME @neomaris/protocol shapes the desktop/mobile
// `<NetworkRecommendation>` renders, emitting the SAME `NetworkApplyDecisions`
// on confirm. It owns only the draft state; every decision is delegated to the
// pure helpers in lib/networkRecommendation.ts (kept structurally identical to
// the desktop reducer). The single platform-varying edge is the injected
// `onApply` — here the cloud apply call.
//
// UX: two STABLE sections, partitioned by write-capability (not by draft role,
// so picking a new primary never reorders cards). The top section ("Choose your
// primary gateway") is the N2K-writable candidates — a radio picks the primary,
// the suggested one keeps a persistent "Recommended" chip. The bottom section
// ("Other data sources") is the read-only sources that can't be primary — a
// plain On/Off toggle only. Nothing is applied until an explicit confirm tap;
// the CTA flips from "Looks good" to "Apply changes" once the draft diverges.
//
// Settings re-entry adds a reevaluate variant: when an `onReevaluate` edge is
// injected, an EDITED draft swaps that "Apply changes" CTA for an ungated
// "Reevaluate" — it POSTs the edits as HARD constraints, the cloud completes
// them into a fresh (always-valid) recommendation the parent remounts us on (via
// `key`), and the user reviews + "Looks good"s THAT. Pins the cloud couldn't
// honor surface as a `constraintConflicts` note on the reevaluated rec. Absent
// (e.g. FTUE) → the legacy edit→apply path stands unchanged.

import { useId, useMemo, useReducer, useState } from 'react';
import type { JSX } from 'react';

import {
  buildApplyDecisions,
  buildConstraints,
  draftRoleFor,
  formatGatewaySentence,
  gatewayExplain,
  humanizeGatewayName,
  initialRecommendationDraft,
  isEdited,
  partitionRows,
  primaryReasonExplain,
  recommendationHeadline,
  reduceRecommendationDraft,
  roleBadgeLabel,
  roleIsActive,
  suggestedPrimaryRow,
  validateDraft,
  type GatewayRow,
  type NetworkApplyDecisions,
  type NetworkUserConstraints,
  type RecommendationDraft,
  type RecommendationDraftAction,
  type SourceFeedRecommendation,
} from '../lib/networkRecommendation';
import { candidateLabel, type RevealCandidate } from '../lib/revealStaging';

export interface NetworkRecommendationProps {
  recommendation: SourceFeedRecommendation;
  /** The ONLY platform-varying edge: persist the confirmed plan. */
  onApply: (decisions: NetworkApplyDecisions) => Promise<void>;
  /** Defer ("Skip for now") — optional. */
  onSkip?: () => void;
  /** Called once the apply resolves successfully. */
  onDone?: () => void;
  /**
   * Re-run the AI against the user's edits as HARD constraints (Settings "Tune
   * your network" reevaluate flow). When provided, an EDITED draft swaps the
   * primary CTA from a validateDraft-gated "Apply changes" to an ungated
   * "Reevaluate": this POSTs the pins, the cloud completes them into a fresh
   * (always-valid) recommendation the parent remounts us on (via `key`). Absent
   * (e.g. FTUE) → the legacy edit→apply path stands.
   */
  onReevaluate?: (constraints: NetworkUserConstraints) => Promise<void>;
  /** True while a submitted reevaluate is in flight — locks the controls. */
  reevaluating?: boolean;
  /**
   * Discovered secured-bridge opt-in offers (bridge_ftue_offer, ADR-078) — a
   * `signalk_n2k_bridge` candidate found on a security-enabled Signal K server
   * that core never auto-creates. Rendered as an explicit, default-OFF row;
   * opting in folds the candidateId into {@link NetworkApplyDecisions.createCandidates}
   * on Apply. Absent/empty (the common case — no secured bridge discovered) →
   * no new UI. See `lib/revealStaging.ts` `securedBridgeOffers`.
   */
  bridgeOffers?: RevealCandidate[];
}

type ApplyStatus = { state: 'idle' } | { state: 'applying' } | { state: 'error'; message: string };

export function NetworkRecommendation({
  recommendation,
  onApply,
  onSkip,
  onDone,
  onReevaluate,
  reevaluating = false,
  bridgeOffers = [],
}: NetworkRecommendationProps): JSX.Element {
  const rec = recommendation;
  const [draft, rawDispatch] = useReducer(
    (state: RecommendationDraft, action: RecommendationDraftAction) =>
      reduceRecommendationDraft(state, action, rec),
    rec,
    initialRecommendationDraft
  );
  const [apply, setApply] = useState<ApplyStatus>({ state: 'idle' });
  // bridge_ftue_offer: which secured-bridge offers the operator has opted
  // into THIS render — local, un-staged until Apply. Keyed by candidateId.
  const [optedInBridgeIds, setOptedInBridgeIds] = useState<ReadonlySet<string>>(new Set());
  const toggleBridgeOffer = (candidateId: string, enabled: boolean): void => {
    setOptedInBridgeIds((prev) => {
      const next = new Set(prev);
      if (enabled) next.add(candidateId);
      else next.delete(candidateId);
      return next;
    });
  };

  const dispatch = (action: RecommendationDraftAction): void => rawDispatch(action);

  // A stable radio-group name prefix shared by every card, so the cards in one
  // bus group form a single native single-select (name per bus group below).
  const uid = useId();
  const headline = recommendationHeadline(rec);
  const validation = validateDraft(rec, draft);
  // Opting into a secured-bridge offer is an edit too (bridge_ftue_offer): it
  // changes what Apply will do (create a source), so the CTA should read
  // "Apply changes" rather than the unconditional "Looks good". But a bridge
  // opt-in is NOT an AI constraint — it folds into `createCandidates` on Apply,
  // not into a reevaluate — so it is kept SEPARATE from `roleEdited` and left out
  // of `reevaluateMode` below (a reevaluate's `buildConstraints` carries no
  // bridge opt-in and would silently drop it).
  const roleEdited = isEdited(rec, draft);
  const edited = useMemo(
    () => roleEdited || optedInBridgeIds.size > 0,
    [roleEdited, optedInBridgeIds]
  );
  const degraded = rec.status === 'degraded';
  // STABLE split by write-capability (NOT by draft role): picking a new primary
  // never moves a card between sections.
  const { candidateRows, otherRows } = useMemo(() => partitionRows(rec), [rec]);
  const suggestedRow = suggestedPrimaryRow(rec);

  // The "why this is your main connection" anchor line under the top heading.
  // When the user has overridden the primary (draft primary ≠ the suggested/LLM
  // pick) the regenerated reason is gated on Apply (design D12), so we show a
  // placeholder rather than a live preview; otherwise the suggestion's reason
  // (LLM-narrated when ready, deterministic when degraded/no-LLM). draftPrimaryId
  // is derived from the draft directly (the partition no longer keys on it).
  const suggestedPrimaryId =
    rec.llm?.preferredN2kGatewaySourceId ??
    rec.rows.find((r) => r.suggestedRole === 'primary_io')?.gatewayId ??
    null;
  const draftPrimaryId =
    rec.rows.find((r) => draftRoleFor(draft, r) === 'primary_io')?.gatewayId ?? null;
  const primaryOverridden = draftPrimaryId !== null && draftPrimaryId !== suggestedPrimaryId;
  const primaryReason = primaryOverridden
    ? 'Apply to see why this is your main connection.'
    : primaryReasonExplain(rec, draft);

  // The surface is "busy" (controls locked) while either an apply or a
  // reevaluate is in flight — a reevaluate is "treated like applying" for every
  // disabled state. `reevaluateMode` is the ROLE-edited-draft + injected-`onReevaluate`
  // case: the primary CTA becomes an ungated "Reevaluate" rather than the
  // validateDraft-gated "Apply changes". Keyed on `roleEdited`, NOT `edited`: a
  // bridge opt-in makes `edited` true (so the label leaves "Looks good") but must
  // NOT switch to reevaluate mode — that path drops the opt-in (buildConstraints
  // has no createCandidates), so a bare bridge opt-in has to stay on the Apply
  // path that folds it in.
  const busy = apply.state === 'applying' || reevaluating;
  const reevaluateMode = roleEdited && onReevaluate !== undefined;

  async function handleApply(): Promise<void> {
    if (busy || !validation.ok) return;
    setApply({ state: 'applying' });
    try {
      await onApply(buildApplyDecisions(rec, draft, Array.from(optedInBridgeIds)));
      setApply({ state: 'idle' });
      onDone?.();
    } catch (err) {
      setApply({ state: 'error', message: applyErrorMessage(err) });
    }
  }

  async function handleReevaluate(): Promise<void> {
    if (!onReevaluate || busy) return;
    setApply({ state: 'idle' });
    try {
      await onReevaluate(buildConstraints(rec, draft));
      // No success reset here: an accepted reevaluate keeps the surface locked
      // (`reevaluating`) until the parent remounts us on the new rec via `key`,
      // which re-seeds an un-edited draft → back to a "Looks good"/apply CTA.
    } catch (err) {
      setApply({ state: 'error', message: reevaluateErrorMessage(err) });
    }
  }

  const ctaLabel = edited ? (reevaluateMode ? 'Reevaluate' : 'Apply changes') : 'Looks good';

  return (
    <section className="net-rec" aria-labelledby="net-rec-title" role="region">
      <h3 className="net-rec__title" id="net-rec-title">
        {headline.title}
      </h3>
      <p className="net-rec__sub">{headline.sub}</p>
      {degraded ? (
        <p className="net-rec__degraded" role="note">
          We couldn&apos;t reach our setup helper — here&apos;s what we worked out from your network
          on its own.
        </p>
      ) : null}

      {rec.constraintConflicts && rec.constraintConflicts.length > 0 ? (
        // A reevaluated rec carries the pins the cloud couldn't honor (illegal /
        // contradictory) — surfaced so the user sees which edits were dropped.
        <div className="net-rec__conflicts" role="note">
          <p className="net-rec__conflicts-headline">We couldn&apos;t make some of your changes:</p>
          <ul>
            {rec.constraintConflicts.map((conflict) => (
              <li key={conflict.sourceId + conflict.message}>{conflict.message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {candidateRows.length > 0 ? (
        <div className="net-rec__group">
          <h4 className="net-rec__group-heading">Choose your primary gateway</h4>
          <p className="net-rec__group-sub">
            These can both read and write on your NMEA 2000 network.
            {suggestedRow
              ? ` We recommend the ${humanizeGatewayName(
                  suggestedRow.name
                )} — pick a different one if you like.`
              : ''}
          </p>
          {primaryReason !== '' ? (
            <p
              className="net-rec__primary-reason"
              data-testid="net-rec-primary-reason"
              aria-live="polite"
            >
              {primaryReason}
            </p>
          ) : null}
          <ul className="net-rec__cards" aria-label="Primary gateway">
            {candidateRows.map((row) => (
              <GatewayCard
                key={row.gatewayId}
                rec={rec}
                row={row}
                draft={draft}
                variant="candidate"
                uid={uid}
                dispatch={dispatch}
                disabled={busy}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {otherRows.length > 0 ? (
        <div className="net-rec__group">
          <h4 className="net-rec__group-heading">
            {candidateRows.length > 0 ? 'Other data sources' : 'Your data sources'}
          </h4>
          {candidateRows.length > 0 ? (
            <p className="net-rec__group-sub">
              These add readings but can&apos;t write to your NMEA 2000 network, so they can&apos;t
              be your primary gateway.
            </p>
          ) : null}
          <ul className="net-rec__cards">
            {otherRows.map((row) => (
              <GatewayCard
                key={row.gatewayId}
                rec={rec}
                row={row}
                draft={draft}
                variant="other"
                uid={uid}
                dispatch={dispatch}
                disabled={busy}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {bridgeOffers.length > 0 ? (
        <div className="net-rec__group" data-testid="net-rec-bridge-offers">
          <h4 className="net-rec__group-heading">Available with approval</h4>
          <p className="net-rec__group-sub">
            We found a raw N2K bridge on a secured Signal K server. Nothing is created unless you
            opt in — and even then, it only starts streaming once you approve it in Signal K.
          </p>
          <ul className="net-rec__cards">
            {bridgeOffers.map((offer) => (
              <BridgeOfferCard
                key={offer.id ?? candidateLabel(offer)}
                candidate={offer}
                optedIn={optedInBridgeIds.has(offer.id ?? '')}
                disabled={busy}
                onToggle={(enabled) => toggleBridgeOffer(offer.id ?? '', enabled)}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {!validation.ok ? (
        <ul className="net-rec__issues" role="alert">
          {validation.issues.map((issue) => (
            <li key={issue.code + (issue.busGroupId ?? '')}>{issue.message}</li>
          ))}
        </ul>
      ) : null}

      {apply.state === 'error' ? (
        <div className="net-rec__error" role="alert">
          <p>{apply.message}</p>
          <button
            type="button"
            className="net-rec__btn"
            onClick={() => void (reevaluateMode ? handleReevaluate() : handleApply())}
          >
            Try again
          </button>
        </div>
      ) : null}

      <div className="net-rec__actions">
        <button
          type="button"
          className="net-rec__btn net-rec__btn--ghost"
          onClick={() => dispatch({ type: 'reset' })}
          disabled={!edited || busy}
        >
          Reset to suggestion
        </button>
        <button
          type="button"
          className="net-rec__btn net-rec__btn--primary"
          onClick={() => void (reevaluateMode ? handleReevaluate() : handleApply())}
          // The reevaluate CTA is NOT validateDraft-gated — the cloud completes
          // the pins into a valid plan — so it's live whenever the draft is
          // edited, locked only while busy. The apply/"Looks good" path keeps its
          // validation gate.
          disabled={reevaluateMode ? busy : !validation.ok || busy}
        >
          {reevaluating ? 'Re-analyzing…' : apply.state === 'applying' ? 'Applying…' : ctaLabel}
        </button>
        {onSkip ? (
          <button
            type="button"
            className="net-rec__btn net-rec__btn--ghost"
            onClick={onSkip}
            disabled={busy}
          >
            Skip for now
          </button>
        ) : null}
      </div>
    </section>
  );
}

function GatewayCard({
  rec,
  row,
  draft,
  variant,
  uid,
  dispatch,
  disabled,
}: {
  rec: SourceFeedRecommendation;
  row: GatewayRow;
  draft: RecommendationDraft;
  variant: 'candidate' | 'other';
  uid: string;
  dispatch: (action: RecommendationDraftAction) => void;
  /** Lock the edit controls while the surface is applying / reevaluating. */
  disabled: boolean;
}): JSX.Element {
  const role = draftRoleFor(draft, row);
  const sentence = formatGatewaySentence(rec, row, draft);
  const active = roleIsActive(role);
  const [whyOpen, setWhyOpen] = useState(false);
  const whyId = useId();
  const explain = whyOpen ? gatewayExplain(rec, row, draft) : null;
  const isRecommended =
    variant === 'candidate' && suggestedPrimaryRow(rec)?.gatewayId === row.gatewayId;

  return (
    <li
      className={`net-rec-card net-rec-card--${role}`}
      data-testid={`net-rec-card-${row.gatewayId}`}
    >
      <div className="net-rec-card__head">
        {variant === 'candidate' ? (
          <label className="net-rec-card__pick">
            <input
              type="radio"
              className="net-rec-card__radio"
              name={`${uid}-${row.busGroupId ?? 'unclustered'}`}
              checked={role === 'primary_io'}
              disabled={disabled}
              aria-label={`Make ${humanizeGatewayName(row.name)} your primary gateway`}
              onChange={() => dispatch({ type: 'makePrimary', gatewayId: row.gatewayId })}
            />
            <span className="net-rec-card__name">{humanizeGatewayName(row.name)}</span>
          </label>
        ) : (
          <span className="net-rec-card__name">{humanizeGatewayName(row.name)}</span>
        )}
        <span className="net-rec-card__aside">
          {isRecommended ? <span className="net-rec-card__chip">Recommended</span> : null}
          <span className={`net-rec-card__badge net-rec-card__badge--${role}`}>
            {roleBadgeLabel(role)}
          </span>
        </span>
      </div>
      <p className="net-rec-card__sentence">{sentence}</p>
      {/* A write-capable candidate turned OFF stays in the picker (dimmed, radio
          still selectable) rather than dropping to the other list — so the copy
          names the one control that does two things: its radio re-enables it. */}
      {variant === 'candidate' && !active ? (
        <p className="net-rec-card__hint">Pick it as primary to turn it back on.</p>
      ) : null}
      {row.parkedReason ? (
        <p
          className="net-rec-card__parked-reason"
          data-testid={`net-rec-parked-reason-${row.gatewayId}`}
        >
          {row.parkedReason}
        </p>
      ) : null}
      {row.warnings.length > 0 ? (
        <ul className="net-rec-card__warnings">
          {row.warnings.map((w, i) => (
            <li key={`${i}-${w}`}>{w}</li>
          ))}
        </ul>
      ) : null}

      <button
        type="button"
        className="net-rec-card__why-toggle"
        aria-expanded={whyOpen}
        aria-controls={whyId}
        onClick={() => setWhyOpen((o) => !o)}
        data-testid={`net-rec-why-toggle-${row.gatewayId}`}
      >
        {whyOpen ? 'Hide details' : 'Why?'}
      </button>
      {whyOpen && explain ? (
        <div className="net-rec-why" id={whyId} data-testid={`net-rec-why-${row.gatewayId}`}>
          <p className="net-rec-why__summary">{explain.summary}</p>
          {explain.provides.length > 0 ? (
            <ul className="net-rec-why__chips" aria-label="Provides">
              {explain.provides.map((p) => (
                <li className="net-rec-why__chip" key={p.key}>
                  {p.label}
                </li>
              ))}
            </ul>
          ) : null}
          {explain.keyCount !== null ? (
            <dl className="net-rec-why__facts">
              <div className="net-rec-why__fact">
                <dt>Readings</dt>
                <dd>{explain.keyCount}</dd>
              </div>
              {explain.uniqueKeyCount !== null ? (
                <div className="net-rec-why__fact">
                  <dt>Only on this gateway</dt>
                  <dd>{explain.uniqueKeyCount}</dd>
                </div>
              ) : null}
              {explain.redundantKeyCount !== null ? (
                <div className="net-rec-why__fact">
                  <dt>Also seen elsewhere</dt>
                  <dd>{explain.redundantKeyCount}</dd>
                </div>
              ) : null}
            </dl>
          ) : null}
          {explain.uniqueKeysSample.length > 0 ? (
            <div className="net-rec-why__unique">
              <span className="net-rec-why__unique-label">Only this gateway provides:</span>
              <ul className="net-rec-why__chips">
                {explain.uniqueKeysSample.map((k) => (
                  <li className="net-rec-why__chip net-rec-why__chip--key" key={k}>
                    {k}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {explain.primaryName !== null ? (
            explain.uniqueVsPrimary.length > 0 ? (
              <div className="net-rec-why__unique">
                <span className="net-rec-why__unique-label">
                  Adds data the {explain.primaryName} doesn&apos;t:
                </span>
                <ul className="net-rec-why__chips">
                  {explain.uniqueVsPrimary.map((k) => (
                    <li className="net-rec-why__chip net-rec-why__chip--key" key={k}>
                      {k}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="net-rec-why__summary">
                Everything it provides is also on {explain.primaryName}.
              </p>
            )
          ) : null}
        </div>
      ) : null}

      {/* On/Off toggle — keyed off roleIsActive(role) (so a `redundant` off state
          reads Off, not On). Rendered on every card EXCEPT the selected primary,
          which is necessarily on. */}
      {role === 'primary_io' ? null : (
        <div className="net-rec-card__controls">
          <label className="net-rec-toggle">
            <input
              type="checkbox"
              checked={active}
              disabled={disabled}
              onChange={(e) =>
                dispatch({
                  type: 'toggleEnabled',
                  gatewayId: row.gatewayId,
                  enabled: e.target.checked,
                })
              }
            />
            <span>{active ? 'On' : 'Off'}</span>
          </label>
        </div>
      )}
    </li>
  );
}

/**
 * The secured-bridge opt-in row (bridge_ftue_offer, ADR-078). Deliberately
 * simpler than {@link GatewayCard} — this candidate isn't a source yet (no
 * overlap facts, no "Why?" panel, no primary radio: it can't be picked as
 * primary until it exists), just a name, the "approval needed" annotation
 * (styled like `GatewayCard`'s `parkedReason`), and a default-OFF toggle.
 */
function BridgeOfferCard({
  candidate,
  optedIn,
  disabled,
  onToggle,
}: {
  candidate: RevealCandidate;
  optedIn: boolean;
  disabled: boolean;
  onToggle: (enabled: boolean) => void;
}): JSX.Element {
  return (
    <li
      className="net-rec-card net-rec-card--bridge-offer"
      data-testid={`net-rec-bridge-offer-${candidate.id ?? ''}`}
    >
      <div className="net-rec-card__head">
        <span className="net-rec-card__name">{candidateLabel(candidate)}</span>
        <span className="net-rec-card__aside">
          <span className="net-rec-card__badge net-rec-card__badge--disabled">
            {optedIn ? 'Opting in' : 'Off'}
          </span>
        </span>
      </div>
      <p
        className="net-rec-card__parked-reason"
        data-testid={`net-rec-bridge-offer-approval-${candidate.id ?? ''}`}
      >
        Raw N2K bridge — available with approval in Signal K admin.
      </p>
      <div className="net-rec-card__controls">
        <label className="net-rec-toggle">
          <input
            type="checkbox"
            checked={optedIn}
            disabled={disabled}
            onChange={(e) => onToggle(e.target.checked)}
            data-testid={`net-rec-bridge-offer-toggle-${candidate.id ?? ''}`}
          />
          <span>{optedIn ? 'On' : 'Off'}</span>
        </label>
      </div>
    </li>
  );
}

function applyErrorMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const e = err as { message?: unknown; body?: { message?: unknown } };
    const serverMsg = typeof e.body?.message === 'string' ? e.body.message : undefined;
    if (serverMsg && serverMsg.length > 0) {
      return `Couldn't apply your changes — ${serverMsg}. You can try again.`;
    }
    if (typeof e.message === 'string' && e.message.length > 0) {
      return `Couldn't apply your changes — ${e.message}. You can try again.`;
    }
  }
  return "Couldn't apply your changes. You can try again.";
}

/**
 * The reevaluate-flow twin of {@link applyErrorMessage}. A 404 from the cloud is
 * the `no_snapshot` case — the analysis it would reevaluate has aged out, so a
 * fresh run from the boat is the way back; everything else reuses the server /
 * thrown message under the "couldn't re-analyze" wording.
 */
function reevaluateErrorMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const e = err as {
      status?: unknown;
      message?: unknown;
      body?: { error?: unknown; message?: unknown };
    };
    if (e.body?.error === 'no_snapshot' || e.status === 404) {
      return "We couldn't re-analyze — the network snapshot has expired. Run setup again from the boat.";
    }
    const serverMsg = typeof e.body?.message === 'string' ? e.body.message : undefined;
    if (serverMsg && serverMsg.length > 0) {
      return `Couldn't re-analyze your network — ${serverMsg}. You can try again.`;
    }
    if (typeof e.message === 'string' && e.message.length > 0) {
      return `Couldn't re-analyze your network — ${e.message}. You can try again.`;
    }
  }
  return "Couldn't re-analyze your network. You can try again.";
}
